/**
 * The plans and what each one includes.
 *
 * ## Why this is not inside the landing page any more
 *
 * Three things now render from it: the pricing section of the landing page, the standalone
 * `/pricing` page, and the `Offer` structured data the pre-render puts in the HTML head. A price is
 * the single worst value on a site to keep two copies of — the copy a customer reads and the copy
 * they are charged have to be the same number, and a search engine quoting a stale one in a result
 * card is a complaint before anybody has signed up.
 *
 * The catalogue in the database is the authority on what is actually charged. These values mirror
 * it and are changed by migration at the same time.
 */

/**
 * Yearly is ten months' money for twelve months' service.
 *
 * Stated as a multiplier rather than twelve hand-written prices so the two cadences cannot drift.
 * A pricing table where the yearly column was edited and the monthly one was not is the kind of
 * error a customer finds at checkout.
 */
export const MONTHS_BILLED_YEARLY = 10;

/** ISO 4217, for structured data. Every price on the site is in this currency. */
export const CURRENCY = 'USD';

/**
 * The free trial every new account starts with.
 *
 * The dashboard reads the trial plan from the catalogue; these pages are rendered before anybody has
 * signed in, so they state its terms here. They are the trial plan's features as the migration that
 * created it set them (`Version20260916000000`): change the plan and these with it, exactly as with a
 * price.
 */
export const TRIAL = {
  days: 7,
  servers: 1,
  monitors: 3,
  commandRuns: 25,
  backupSources: 2,
} as const;

/**
 * The free trial as a column of the pricing table, beside the plans it leads to.
 *
 * A column rather than a sentence above the table: it is what every sign-up starts on, so it is
 * compared row by row with what the plans include, and its limits are read in the same place as
 * theirs rather than discovered by being refused something.
 */
export const TRIAL_PLAN = {
  name: 'Free Trial',
  blurb: 'For trying the whole platform on one server.',
  cta: 'Start free trial',
} as const;

export interface Plan {
  name: string;
  /** Price per month when billed monthly, in whole units of {@link CURRENCY}. */
  monthly: number;
  blurb: string;
  cta: string;
  highlighted: boolean;
  /**
   * The yearly plan's own price in minor units, when the catalogue has one.
   *
   * Null in the values written here, where {@link MONTHS_BILLED_YEARLY} decides it. A catalogue is
   * free to price a year independently of twelve months, and if it does, that price is what the
   * yearly view must show — the checkout charges it either way.
   */
  yearly?: number | null;
}

export const PLANS: Plan[] = [
  {
    name: 'Starter',
    monthly: 12,
    blurb: 'For one person managing a few servers.',
    cta: 'Choose Starter',
    highlighted: false,
  },
  {
    name: 'Pro',
    monthly: 29,
    blurb: 'For freelancers and agencies managing client servers.',
    cta: 'Choose Pro',
    highlighted: true,
  },
  {
    name: 'Business',
    monthly: 49,
    blurb: 'For larger fleets and infrastructure teams.',
    cta: 'Choose Business',
    highlighted: false,
  },
];

/** The price of a plan for a full year, billed once. */
export function yearlyPrice(plan: Plan): number {
  return plan.monthly * MONTHS_BILLED_YEARLY;
}

/** A tick (`true`), a cross (`false`), or a value printed as it is. */
export type Cell = boolean | string;

/**
 * How a plan feature reads in its row.
 *
 * `count` prints the number and "Unlimited" for -1; `people` says "Just you" for one, because a
 * limit of 1 is the account owner and nobody else; `yesno` is a tick or a cross.
 */
export type Reads = 'count' | 'people' | 'yesno';

export interface ComparisonRow {
  label: string;
  trial: Cell;
  plans: Cell[];
  /**
   * The entitlement key this row shows, where the catalogue is the authority for it.
   *
   * The values above are what the pre-rendered HTML carries, so a crawler and a first paint see
   * something true as of the build. The live catalogue replaces them as soon as it answers — which
   * is what makes raising a limit in the admin catalogue show up here without a deploy.
   */
  feature?: string;
  reads?: Reads;
}

/**
 * The comparison table, one row per thing the platform does.
 *
 * ## Every row is a feature that exists
 *
 * A pricing table is where a customer decides, and it is the worst place on the site to describe
 * something that has not been built. Each row below corresponds to a module in this repository, and
 * the counts to the quotas the Catalog module hands out. `trial` is the free trial's column; `plans`
 * are Starter, Pro and Business in {@link PLANS} order.
 *
 * ## A cross is as useful as a tick
 *
 * A table of nothing but ticks tells a reader only that the page wants their money. The crosses
 * here are the ones that make the ticks mean something — and each is a genuine boundary, not a
 * feature withheld to force an upgrade of something already built for everyone.
 *
 * Nothing here promises a level of support: nothing in the platform treats one plan's tickets
 * differently from another's, so a "Priority support" row was removed rather than left as a claim.
 */
export const COMPARISON: {
  group: string;
  rows: ComparisonRow[];
}[] = [
  {
    group: 'What it covers',
    rows: [
      { label: 'Servers', trial: String(TRIAL.servers), plans: ['5', '25', 'Unlimited'], feature: 'servers', reads: 'count' },
      { label: 'Monitors', trial: String(TRIAL.monitors), plans: ['10', '50', '200'], feature: 'monitors', reads: 'count' },
      {
        label: 'Backup sources (a directory, a database, or both)',
        trial: String(TRIAL.backupSources),
        plans: ['10', '50', 'Unlimited'],
        feature: 'backup_sources',
        reads: 'count',
      },
      { label: 'People on the account', trial: 'Just you', plans: ['Just you', '5', 'Unlimited'], feature: 'team_members', reads: 'people' },
      {
        label: 'Command Runner runs',
        trial: String(TRIAL.commandRuns),
        plans: ['Unlimited', 'Unlimited', 'Unlimited'],
        feature: 'command_runs',
        reads: 'count',
      },
    ],
  },
  {
    group: 'Automation',
    rows: [
      { label: 'Playbooks for Ubuntu, Debian and RHEL-compatible servers', trial: true, plans: [true, true, true] },
      { label: 'Security hardening with Fail2Ban, a firewall and SSH settings', trial: true, plans: [true, true, true] },
      { label: 'Server templates, including Python, reverse proxy and CI runner hosts', trial: true, plans: [true, true, true] },
      { label: 'Command runner across servers', trial: true, plans: [true, true, true] },
      { label: 'Preflight, dry run, canary and batches', trial: true, plans: [true, true, true] },
      { label: 'Run history with every server’s output', trial: true, plans: [true, true, true] },
    ],
  },
  {
    group: 'Monitoring and auto-fix',
    rows: [
      { label: 'Six monitor types, including server health', trial: true, plans: [true, true, true] },
      { label: 'Auto-Fix before alerting', trial: true, plans: [true, true, true] },
      { label: 'Email and webhook alerts', trial: true, plans: [true, true, true] },
      { label: 'Incident history', trial: true, plans: [true, true, true] },
    ],
  },
  {
    group: 'Backups and verification',
    rows: [
      { label: 'Directories and databases, to storage you own', trial: true, plans: [true, true, true] },
      { label: 'S3-compatible storage, or the server itself', trial: true, plans: [true, true, true] },
      { label: 'Weekly restore tests', trial: true, plans: [true, true, true] },
      { label: 'Restore onto a different server', trial: true, plans: [true, true, true] },
      // The one part of backups the trial leaves out, and the only one that moves bytes through
      // the platform. Everything that protects the data is above this line.
      {
        label: 'Download an archive through Techie Gain',
        trial: false,
        plans: [true, true, true],
        feature: 'backup_downloads',
        reads: 'yesno',
      },
    ],
  },
  {
    group: 'Account',
    rows: [
      // The two that actually differ. Team management is the reason Pro exists, and API access
      // is the reason Business does.
      { label: 'Team management', trial: false, plans: [false, true, true] },
      { label: 'API access', trial: false, plans: [false, false, true], feature: 'api_access', reads: 'yesno' },
    ],
  },
];
